import {useMemo} from "react";
import {Link} from "react-router-dom";
import {decodeAbiParameters, parseAbiParameters} from "viem";
import {useFilingCount, useFilingKeyAt, useFilingByKey} from "@/hooks/useSmeltReads";
import {SMELT_CONTRACT} from "@/lib/config";

/**
 * Live filing list, read directly from the contract (no mock data). Enumerates
 * filingCount -> filingKeyAt -> filingByKey. Reads are gated and staggered to
 * avoid bursts on page load.
 */
export function Filings() {
  const countQ = useFilingCount();
  const count = countQ.data ? Number(countQ.data) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-forge-ash">Filings</h1>
          <p className="text-sm text-forge-slag mt-1">Structured filings stored on-chain, read live from the contract.</p>
        </div>
        <span className="label">{count} stored</span>
      </div>

      {SMELT_CONTRACT === "0x0000000000000000000000000000000000000000" && (
        <div className="panel p-4 text-sm text-forge-ash">
          Contract address not configured. Deploy Smelt and set{" "}
          <span className="mono">VITE_SMELT_CONTRACT</span> (or bake it into{" "}
          <span className="mono">src/lib/config.ts</span>).
        </div>
      )}

      {count === 0 && SMELT_CONTRACT !== "0x0000000000000000000000000000000000000000" && (
        <div className="panel p-8 text-center text-forge-slag">
          No filings on-chain yet. After deploy, the scheduled pipeline refines the
          first target and a filing appears here.
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({length: count}, (_, i) => (
          <FilingCard key={i} index={i} />
        ))}
      </div>
    </div>
  );
}

function FilingCard({index}: {index: number}) {
  const keyQ = useFilingKeyAt(index);
  const key = keyQ.data as `0x${string}` | undefined;
  const filingQ = useFilingByKey(key);

  const metrics = useMemo(() => extractMetrics(filingQ.data), [filingQ.data]);

  if (!filingQ.data) {
    return <div className="panel p-5 h-40 animate-pulseheat bg-forge-surface/40" />;
  }

  const f = filingQ.data as any;
  if (!f?.exists) {
    return (
      <div className="panel p-5">
        <p className="text-sm text-forge-ash">Empty slot {index}</p>
      </div>
    );
  }

  return (
    <div className="panel-elevated p-5 flex flex-col gap-3 hover:border-molten-700/50 transition-colors">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-xl text-forge-ash">{f.ticker}</h3>
        <span className="font-mono text-xs text-forge-ash">{f.period}</span>
      </div>
      <div className="divider" />
      {f.valid && metrics ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {metrics.map((m) => (
            <div key={m.name}>
              <dt className="label">{m.name}</dt>
              <dd className="font-mono text-refined-cyan">{m.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-sm text-molten-400 mono">parse pending / retry queued</p>
      )}
      <Link to="/console" state={{ticker: f.ticker, period: f.period}} className="btn-ghost mt-1 self-start text-xs">
        Query →
      </Link>
    </div>
  );
}

interface Metric {
  name: string;
  value: string;
}

/**
 * The contract stores the raw ABI-encoded LLM completionData (an OpenAI-style
 * completion struct). Extracting the content string on-chain is expensive, so we
 * decode it off-chain here: completionData -> choices[0].message.content -> JSON.
 */
function extractMetrics(filing: unknown): Metric[] | null {
  try {
    const f = filing as any;
    if (!f?.exists || !f?.valid || !f?.completionData || f.completionData === "0x") return null;

    // completionData: (string id, string object, uint256 created, string model,
    //   string systemFingerprint, string serviceTier, uint256 choicesCount,
    //   bytes[] choicesData, bytes usageData)
    const decoded = decodeAbiParameters(
      parseAbiParameters(
        "string, string, uint256, string, string, string, uint256, bytes[], bytes"
      ),
      f.completionData
    );
    const choicesCount = Number(decoded[6]);
    if (choicesCount === 0 || decoded[7].length === 0) return null;

    // each choice: (uint256 index, string finishReason, bytes messageData)
    const choice = decodeAbiParameters(parseAbiParameters("uint256, string, bytes"), decoded[7][0]);
    // messageData: (string role, string content, string refusal, uint256 toolCallsCount, bytes[])
    const message = decodeAbiParameters(
      parseAbiParameters("string, string, string, uint256, bytes[]"),
      choice[2]
    );
    const content = message[1] as string;
    const json = JSON.parse(content) as Record<string, number | null>;

    return [
      {name: "Revenue", value: fmt(json.Revenue)},
      {name: "Net Income", value: fmt(json.NetIncome)},
      {name: "Total Debt", value: fmt(json.TotalDebt)},
      {name: "EPS", value: fmt(json.EPS)},
      {name: "Free Cash Flow", value: fmt(json.FreeCashFlow)},
    ];
  } catch {
    return null;
  }
}

function fmt(v: number | null | undefined): string {
  if (v === null || v === undefined) return "null";
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + "M";
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(2) + "K";
  return String(v);
}
